import { Matrix4 } from 'three';

import {
  classifyStlJaw,
  classifyStlJawFromToothNumbers,
  STL_IDENTITY_MATRIX,
  type StlJaw,
  type StlLayerColor,
  type StlLayoutConfig,
  type StlMatrix4,
  getSupportedViewerFileFormat,
} from './stl-layout';

const MATRIX_ELEMENT_NAMES = [
  '_00',
  '_01',
  '_02',
  '_03',
  '_10',
  '_11',
  '_12',
  '_13',
  '_20',
  '_21',
  '_22',
  '_23',
  '_30',
  '_31',
  '_32',
  '_33',
] as const;

export interface StlConstructionFileMatrix {
  fileName: string | null;
  jaw: StlJaw;
  matrix: StlMatrix4;
  color?: StlLayerColor;
}

interface ConstructionInfoFile {
  fileName: string | null;
  jaw: StlJaw;
  color: StlLayerColor;
  matrix: Matrix4 | null;
}

export function parseConstructionInfoMatrices(xml: string): StlConstructionFileMatrix[] {
  if (typeof DOMParser === 'undefined') {
    return [];
  }

  const document = new DOMParser().parseFromString(xml, 'application/xml');
  if (document.querySelector('parsererror')) {
    return [];
  }

  // Matrix meanings and the All-In-One assumption are documented in ../../README.md#design-review-stl-coordinates.
  // Keep scans and exported dentures separate here: their matrices have different source/target frames.
  const scans = readFiles(document, 'ScanFiles', 'ScanFile', 'TransformationMatrix', 'scan');
  const dentures = readFiles(
    document,
    'ConstructionFileList',
    'ConstructionFile',
    'ZRotationMatrix',
    'model',
  );
  // Aesthetic plates are not jaw scans; one remaining unassigned jaw scan identifies All-In-One input.
  const jawScans = scans.filter((scan) => scan.color === 'scan');
  const allInOneScan = jawScans.length === 1 && jawScans[0]?.jaw === 'other' ? jawScans[0] : undefined;

  const dentureMatrices = dentures.flatMap((denture): StlConstructionFileMatrix[] => {
    if (!denture.matrix && !denture.fileName) {
      return [];
    }

    // ZRotationMatrix is input-scan -> denture (I2D); the viewer needs denture -> input-scan.
    const matrix = denture.matrix?.clone().invert() ?? new Matrix4();
    const inputToCad = jawScans.find((scan) => scan.jaw === denture.jaw)?.matrix ?? allInOneScan?.matrix;
    if (denture.matrix && inputToCad) {
      // Compose CAD * inverse(I2D); for All-In-One, the shared I2C is used for either jaw.
      // An All-In-One scan matrix belongs to the CAD-cut lower-jaw component.
      // Its unknown relative jaw movement is intentionally not reconstructed: both dentures
      // use the shared scan matrix with their own inverse I2D matrix; there is no U2L matrix.
      matrix.premultiply(inputToCad);
    }

    return [
      {
        fileName: denture.fileName,
        jaw: denture.jaw,
        color: 'model',
        matrix: matrix.toArray() as StlMatrix4,
      },
    ];
  });

  return [
    ...dentureMatrices,
    ...scans.map((scan) => ({
      fileName: scan.fileName,
      jaw: scan.jaw,
      color: scan.color,
      matrix: scan.matrix ? (scan.matrix.toArray() as StlMatrix4) : STL_IDENTITY_MATRIX,
    })),
  ];
}

export function applyConstructionInfoMatrices(
  config: StlLayoutConfig,
  matrices: readonly StlConstructionFileMatrix[],
): StlLayoutConfig {
  if (matrices.length === 0) {
    return config;
  }

  const jawMatrices = new Map<StlJaw, StlMatrix4>();
  for (const constructionFile of matrices) {
    if (
      (constructionFile.color === undefined || constructionFile.color === 'model') &&
      (constructionFile.jaw === 'upper' || constructionFile.jaw === 'lower') &&
      !jawMatrices.has(constructionFile.jaw)
    ) {
      jawMatrices.set(constructionFile.jaw, constructionFile.matrix);
    }
  }

  const layerOverrides = matrices
    .filter((constructionFile) => Boolean(constructionFile.fileName))
    .map((constructionFile) => ({
      fileName: constructionFile.fileName as string,
      ...(constructionFile.jaw !== 'other' ? { jaw: constructionFile.jaw } : {}),
      ...(constructionFile.color ? { color: constructionFile.color } : {}),
      matrix: constructionFile.matrix,
    }));
  const hasJawSpecificMatrix = jawMatrices.size > 0;
  const unassignedMatrix = matrices.find(
    (constructionFile) =>
      constructionFile.jaw === 'other' &&
      !constructionFile.fileName &&
      (constructionFile.color === undefined || constructionFile.color === 'model'),
  )?.matrix;
  const upperMatrix = jawMatrices.get('upper');
  const lowerMatrix = jawMatrices.get('lower');

  return {
    ...config,
    sceneMatrix: unassignedMatrix && !hasJawSpecificMatrix ? unassignedMatrix : config.sceneMatrix,
    hasConstructionInfoSceneMatrix:
      (unassignedMatrix !== undefined && !hasJawSpecificMatrix) || config.hasConstructionInfoSceneMatrix,
    layerOverrides: layerOverrides.length > 0 ? layerOverrides : config.layerOverrides,
    jaws: {
      ...config.jaws,
      upper: {
        ...config.jaws.upper,
        ...(upperMatrix ? { matrix: upperMatrix, hasConstructionInfoMatrix: true } : {}),
      },
      lower: {
        ...config.jaws.lower,
        ...(lowerMatrix ? { matrix: lowerMatrix, hasConstructionInfoMatrix: true } : {}),
      },
    },
  };
}

function readFiles(
  document: Document,
  listName: string,
  elementName: string,
  matrixName: string,
  defaultColor: StlLayerColor,
): ConstructionInfoFile[] {
  // This shared reader preserves each XML list's own matrix field and assigns its display category.
  const list = findElements(document, listName)[0];
  if (!list) {
    return [];
  }

  return childElements(list, elementName).flatMap((element) => {
    const fileName = childText(element, 'Filename') || null;
    if (
      (defaultColor === 'scan' && !fileName) ||
      (fileName && getSupportedViewerFileFormat(fileName) === undefined)
    ) {
      return [];
    }
    const metadata = ['PartType', 'PartName', 'LoadedBy', 'DentureType']
      .map((name) => childText(element, name) ?? '')
      .join(' ')
      .toLowerCase()
      .replace(/[^a-z]/g, '');
    const normalizedName = (fileName ?? '').toLowerCase().replace(/[^a-z]/g, '');
    const isPlate =
      `${metadata}${normalizedName}`.includes('aestheticplate') ||
      `${metadata}${normalizedName}`.includes('estheticplate');
    const isCombined = `${metadata}${normalizedName}`.includes('allinone');
    const jaw = isPlate || isCombined ? 'other' : readJaw(element, metadata, fileName);
    const matrixElement = childElements(element, matrixName)[0];

    return [
      {
        fileName,
        jaw,
        color: defaultColor === 'scan' && isPlate ? 'aestheticPlate' : defaultColor,
        matrix: matrixElement ? parseMatrix(matrixElement) : null,
      },
    ];
  });
}

function readJaw(element: Element, metadata: string, fileName: string | null): StlJaw {
  // Prefer explicit XML type/name metadata, then FDI teeth, and only then infer from the filename.
  const hasUpper = metadata.includes('upper');
  const hasLower = metadata.includes('lower');
  if (hasUpper || hasLower) {
    return hasUpper && hasLower ? 'other' : hasUpper ? 'upper' : 'lower';
  }

  const toothNumbers = parseToothNumbers(element);
  const fdiJaw = classifyStlJawFromToothNumbers(toothNumbers);
  if (
    fdiJaw !== 'other' ||
    toothNumbers.some((tooth) => classifyStlJawFromToothNumbers([tooth]) !== 'other')
  ) {
    return fdiJaw;
  }

  return classifyStlJaw(fileName ?? '');
}

function parseMatrix(matrixElement: Element): Matrix4 | null {
  const values = MATRIX_ELEMENT_NAMES.map((name) => {
    const value = childText(matrixElement, name);
    return !value ? Number.NaN : Number(value.trim().replace(',', '.'));
  }) as StlMatrix4;

  if (values.some((value) => !Number.isFinite(value))) {
    return null;
  }

  const matrix = new Matrix4().fromArray(values);
  // Singular matrices cannot be inverted for exported dentures or used as a stable scan transform.
  if (!Number.isFinite(matrix.determinant()) || Math.abs(matrix.determinant()) < 1e-12) {
    return null;
  }

  return matrix;
}

function parseToothNumbers(constructionFile: Element): number[] {
  const toothNumbersElement = childElements(constructionFile, 'ToothNumbers')[0];
  if (!toothNumbersElement) {
    return [];
  }

  return findElements(toothNumbersElement, 'int').flatMap((element) => {
    const value = Number(element.textContent?.trim() ?? '');
    return Number.isInteger(value) ? [value] : [];
  });
}

function childElements(parent: Element, name: string): Element[] {
  return Array.from(parent.children).filter(
    (element) => element.localName.toLocaleLowerCase() === name.toLocaleLowerCase(),
  );
}

function childText(parent: Element, name: string): string | null {
  return childElements(parent, name)[0]?.textContent?.trim() ?? null;
}

function findElements(parent: Document | Element, name: string): Element[] {
  return Array.from(parent.getElementsByTagName('*')).filter(
    (element) => element.localName.toLocaleLowerCase() === name.toLocaleLowerCase(),
  );
}
